import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
    BookOpen, Home, AlertCircle, LayoutDashboard, LogOut,
    SpellCheck, User, Menu, X, GraduationCap, Layout, Hammer, Settings,
    AlertTriangle, Lock, ChevronDown, Calendar, KeyRound
} from 'lucide-react';
import { Button } from './ui/button';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useFeatureAccess, FEATURE_ROUTE_MAP } from '../context/FeatureAccessContext';
import LockedFeatureModal from './LockedFeatureModal';
import axios from 'axios';
import { toast } from 'sonner';

const FEATURE_META = {
    sections: { name: '৩০ সেকশন', icon: Layout, gradient: 'from-emerald-500 to-teal-500' },
    builder: { name: 'বাক্য নির্মাণ', icon: Hammer, gradient: 'from-blue-500 to-indigo-500' },
    lesson: { name: 'পাঠ', icon: BookOpen, gradient: 'from-purple-500 to-violet-500' },
    drills: { name: 'অনুশীলন', icon: AlertCircle, gradient: 'from-orange-500 to-amber-500' },
    spelling: { name: 'স্পেলিং', icon: SpellCheck, gradient: 'from-pink-500 to-rose-500' },
    mistakes: { name: 'সাধারণ ভুল', icon: AlertTriangle, gradient: 'from-red-500 to-rose-600' },
};

const tb = { fontFamily: "'Tiro Bangla', serif" };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatValidity = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const isExpiredSoon = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
    return diff <= 7 && diff >= 0;
};

const Navbar = () => {
    const { user, logout, isAuthenticated } = useAuth();
    const { isLocked } = useFeatureAccess();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const [showChangePwd, setShowChangePwd] = React.useState(false);
    const [pwdForm, setPwdForm] = React.useState({ current: '', newPwd: '', confirm: '' });
    const [pwdLoading, setPwdLoading] = React.useState(false);
    const [lockedModalKey, setLockedModalKey] = React.useState(null);

    const handleLogout = () => { logout(); navigate('/'); };

    const handleChangePwd = async (e) => {
        e.preventDefault();
        if (pwdForm.newPwd !== pwdForm.confirm) { toast.error('নতুন পাসওয়ার্ড দুটি মিলছে না'); return; }
        setPwdLoading(true);
        try {
            await axios.put(`${API}/student/change-password`, { current_password: pwdForm.current, new_password: pwdForm.newPwd });
            toast.success('পাসওয়ার্ড সফলভাবে পরিবর্তন হয়েছে!');
            setPwdForm({ current: '', newPwd: '', confirm: '' });
            setShowChangePwd(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'পরিবর্তন ব্যর্থ হয়েছে');
        }
        setPwdLoading(false);
    };

    const studentLinks = [
        { path: '/dashboard', label: 'Home', labelBn: 'হোম', icon: Home },
        { path: '/learning-hub', label: '30 Sections', labelBn: '৩০ সেকশন', icon: Layout },
        { path: '/sentence-builder', label: 'Builder', labelBn: 'বাক্য নির্মাণ', icon: Hammer },
        { path: '/lesson', label: 'Lesson', labelBn: 'পাঠ', icon: BookOpen },
        { path: '/drills', label: 'Drills', labelBn: 'অনুশীলন', icon: AlertCircle },
    ];

    const teacherLinks = [
        { path: '/dashboard', label: 'Admin Panel', labelBn: 'অ্যাডমিন প্যানেল', icon: LayoutDashboard },
        { path: '/admin/settings', label: 'Settings', labelBn: 'সেটিংস', icon: Settings },
    ];

    const links = (user?.role === 'teacher' || user?.role === 'moderator') ? teacherLinks : studentLinks;

    if (!isAuthenticated) return null;

    return (
        <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100/80" data-testid="main-navbar">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">

                {/* ===== LOGO ===== */}
                <Link to="/dashboard" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
                    <img src="/images/logo.png" alt="বাক্য শিল্পী" className="h-9 w-auto" />
                    <span className="font-bold text-lg text-gray-800 hidden sm:hidden" style={tb}>
                        বাক্য শিল্পী
                    </span>
                </Link>

                {/* ===== DESKTOP NAV LINKS ===== */}
                <div className="hidden md:flex items-center gap-1">
                    {links.map((link) => {
                        const Icon = link.icon;
                        const isActive = location.pathname === link.path;
                        const featureKey = FEATURE_ROUTE_MAP[link.path];
                        const locked = featureKey && isLocked(featureKey);
                        return (
                            <Link
                                key={link.path}
                                to={locked ? '#' : link.path}
                                onClick={e => {
                                    if (locked) { e.preventDefault(); setLockedModalKey(featureKey); }
                                }}
                                data-testid={`nav-${link.path.replace('/', '')}`}
                                className={`relative flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                                    locked
                                        ? 'opacity-50 cursor-default text-gray-400 hover:bg-amber-50'
                                        : isActive
                                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/25'
                                            : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                                }`}
                            >
                                {locked
                                    ? <Lock className="w-3.5 h-3.5 shrink-0" />
                                    : <Icon className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-white' : ''}`} />
                                }
                                <span>{link.label}</span>
                                {locked && (
                                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-gray-300 rounded-full flex items-center justify-center">
                                        <Lock className="w-2 h-2 text-gray-500" />
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </div>

                {/* ===== RIGHT SIDE ===== */}
                <div className="flex items-center gap-2">
                    {/* Spelling Quick Link */}
                    {user?.role === 'student' && (
                        isLocked('spelling') ? (
                            <button
                                onClick={() => setLockedModalKey('spelling')}
                                data-testid="nav-spelling-builder"
                                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all opacity-50 bg-gray-100 text-gray-400 cursor-default hover:bg-amber-50"
                            >
                                <Lock className="w-3.5 h-3.5 shrink-0" />
                                <span>Spelling</span>
                            </button>
                        ) : (
                            <Link
                                to="/spelling-builder"
                                data-testid="nav-spelling-builder"
                                className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                                    location.pathname === '/spelling-builder'
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                                }`}
                            >
                                <SpellCheck className="w-3.5 h-3.5 shrink-0" />
                                <span>Spelling</span>
                            </Link>
                        )
                    )}

                    {/* Mistakes Quick Link */}
                    {user?.role === 'student' && (
                        isLocked('mistakes') ? (
                            <button
                                onClick={() => setLockedModalKey('mistakes')}
                                data-testid="nav-common-mistakes"
                                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all opacity-50 bg-gray-100 text-gray-400 cursor-default hover:bg-amber-50"
                            >
                                <Lock className="w-3.5 h-3.5 shrink-0" />
                                <span>Mistakes</span>
                            </button>
                        ) : (
                            <Link
                                to="/common-mistakes"
                                data-testid="nav-common-mistakes"
                                className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                                    location.pathname === '/common-mistakes'
                                        ? 'bg-rose-600 text-white shadow-sm'
                                        : 'bg-rose-50 text-rose-600 hover:bg-rose-100'
                                }`}
                            >
                                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                <span>Mistakes</span>
                            </Link>
                        )
                    )}

                    {/* User Dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="flex items-center gap-2 px-2.5 py-1.5 rounded-full hover:bg-purple-50 transition-all group" data-testid="user-menu-trigger">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 border border-purple-200/60 flex items-center justify-center">
                                    <User className="w-3.5 h-3.5 text-purple-600" />
                                </div>
                                <span className="hidden sm:block text-sm font-medium text-gray-700 group-hover:text-purple-600 transition-colors max-w-[100px] truncate">
                                    {user?.name}
                                </span>
                                <ChevronDown className="hidden sm:block w-3.5 h-3.5 text-gray-400 group-hover:text-purple-500 transition-colors" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-60 rounded-2xl shadow-xl border border-gray-100 p-1">
                            <div className="px-3 py-2.5 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 mb-1">
                                <p className="font-semibold text-sm text-gray-800 truncate">{user?.name}</p>
                                <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                                {user?.validity_date && (
                                    <div className={`flex items-center gap-1 mt-1.5 text-[11px] font-medium ${isExpiredSoon(user.validity_date) ? 'text-orange-500' : 'text-purple-600'}`}>
                                        <Calendar className="w-3 h-3" />
                                        <span style={tb}>Validity Till: {formatValidity(user.validity_date)}</span>
                                        {isExpiredSoon(user.validity_date) && <span className="text-orange-400">⚠️</span>}
                                    </div>
                                )}
                            </div>
                            <DropdownMenuSeparator className="my-1" />
                            {user?.role === 'student' && (
                                <DropdownMenuItem
                                    onClick={() => setShowChangePwd(true)}
                                    className="cursor-pointer rounded-lg hover:bg-purple-50 focus:bg-purple-50 text-gray-600"
                                    data-testid="change-password-btn"
                                >
                                    <KeyRound className="w-4 h-4 mr-2 text-purple-500" />
                                    পাসওয়ার্ড পরিবর্তন
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                onClick={handleLogout}
                                className="text-rose-600 cursor-pointer rounded-lg hover:bg-rose-50 focus:bg-rose-50"
                                data-testid="logout-btn"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Change Password Dialog */}
                    <Dialog open={showChangePwd} onOpenChange={setShowChangePwd}>
                        <DialogContent className="max-w-sm rounded-2xl">
                            <DialogHeader>
                                <DialogTitle style={tb}>পাসওয়ার্ড পরিবর্তন করুন</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleChangePwd} className="space-y-4 pt-1">
                                <div className="space-y-1.5">
                                    <Label className="text-sm" style={tb}>বর্তমান পাসওয়ার্ড</Label>
                                    <Input type="password" placeholder="বর্তমান পাসওয়ার্ড" value={pwdForm.current}
                                        onChange={e => setPwdForm(p => ({...p, current: e.target.value}))} required className="rounded-xl" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-sm" style={tb}>নতুন পাসওয়ার্ড</Label>
                                    <Input type="password" placeholder="নতুন পাসওয়ার্ড (কমপক্ষে ৬ অক্ষর)" value={pwdForm.newPwd}
                                        onChange={e => setPwdForm(p => ({...p, newPwd: e.target.value}))} required minLength={6} className="rounded-xl" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-sm" style={tb}>পাসওয়ার্ড নিশ্চিত করুন</Label>
                                    <Input type="password" placeholder="আবার দিন" value={pwdForm.confirm}
                                        onChange={e => setPwdForm(p => ({...p, confirm: e.target.value}))} required className="rounded-xl" />
                                </div>
                                <DialogFooter className="gap-2">
                                    <Button type="button" variant="outline" className="rounded-full flex-1" onClick={() => setShowChangePwd(false)} style={tb}>বাতিল</Button>
                                    <Button type="submit" disabled={pwdLoading}
                                        className="rounded-full flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white" style={tb}>
                                        {pwdLoading ? '...' : 'পরিবর্তন করুন'}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>

                    {/* Mobile Hamburger */}
                    <button
                        className="md:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-purple-50 text-gray-600 hover:text-purple-600 transition-all"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        data-testid="mobile-menu-btn"
                    >
                        {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {/* ===== MOBILE MENU ===== */}
            {mobileMenuOpen && (
                <div className="md:hidden border-t border-gray-100 bg-white animate-slide-up">
                    <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
                        {links.map((link) => {
                            const Icon = link.icon;
                            const isActive = location.pathname === link.path;
                            const featureKey = FEATURE_ROUTE_MAP[link.path];
                            const locked = featureKey && isLocked(featureKey);
                            return (
                                <Link
                                    key={link.path}
                                    to={locked ? '#' : link.path}
                                    onClick={(e) => {
                                        if (locked) { e.preventDefault(); setLockedModalKey(featureKey); setMobileMenuOpen(false); }
                                        else { setMobileMenuOpen(false); }
                                    }}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                                        locked ? 'opacity-40 cursor-not-allowed text-gray-400' :
                                        isActive
                                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20'
                                            : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                                    }`}
                                >
                                    {locked ? <Lock className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                                    <div>
                                        <span className="font-medium block text-sm">{link.label}</span>
                                        <span className="text-xs opacity-60" style={tb}>{link.labelBn}</span>
                                    </div>
                                    {locked && <span className="ml-auto text-[10px] bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">Locked</span>}
                                </Link>
                            );
                        })}

                        {/* Mobile quick links */}
                        {user?.role === 'student' && (
                            isLocked('spelling') ? (
                                <button
                                    onClick={() => { setLockedModalKey('spelling'); setMobileMenuOpen(false); }}
                                    className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all opacity-40 cursor-not-allowed text-gray-400"
                                >
                                    <Lock className="w-5 h-5" />
                                    <div>
                                        <span className="font-medium block text-sm">Spelling</span>
                                        <span className="text-xs opacity-60" style={tb}>স্পেলিং বিল্ডার</span>
                                    </div>
                                    <span className="ml-auto text-[10px] bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">Locked</span>
                                </button>
                            ) : (
                                <Link
                                    to="/spelling-builder"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                                        location.pathname === '/spelling-builder'
                                            ? 'bg-indigo-600 text-white'
                                            : 'text-indigo-600 hover:bg-indigo-50'
                                    }`}
                                >
                                    <SpellCheck className="w-5 h-5" />
                                    <div>
                                        <span className="font-medium block text-sm">Spelling</span>
                                        <span className="text-xs opacity-60" style={tb}>স্পেলিং বিল্ডার</span>
                                    </div>
                                </Link>
                            )
                        )}

                        {user?.role === 'student' && (
                            isLocked('mistakes') ? (
                                <button
                                    onClick={() => { setLockedModalKey('mistakes'); setMobileMenuOpen(false); }}
                                    className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all opacity-40 cursor-not-allowed text-gray-400"
                                >
                                    <Lock className="w-5 h-5" />
                                    <div>
                                        <span className="font-medium block text-sm">Mistakes</span>
                                        <span className="text-xs opacity-60" style={tb}>কমন ভুল</span>
                                    </div>
                                    <span className="ml-auto text-[10px] bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">Locked</span>
                                </button>
                            ) : (
                                <Link
                                    to="/common-mistakes"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                                        location.pathname === '/common-mistakes'
                                            ? 'bg-rose-600 text-white'
                                            : 'text-rose-600 hover:bg-rose-50'
                                    }`}
                                >
                                    <AlertTriangle className="w-5 h-5" />
                                    <div>
                                        <span className="font-medium block text-sm">Mistakes</span>
                                        <span className="text-xs opacity-60" style={tb}>কমন ভুল</span>
                                    </div>
                                </Link>
                            )
                        )}

                        {/* Mobile logout */}
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 px-4 py-3 rounded-xl text-rose-500 hover:bg-rose-50 transition-all mt-1"
                            data-testid="logout-btn-mobile"
                        >
                            <LogOut className="w-5 h-5" />
                            <span className="font-medium text-sm">Logout</span>
                        </button>
                    </div>
                </div>
            )}
            {/* ===== LOCKED FEATURE MODAL ===== */}
            <LockedFeatureModal
                open={!!lockedModalKey}
                onClose={() => setLockedModalKey(null)}
                featureName={lockedModalKey ? FEATURE_META[lockedModalKey]?.name : ''}
                icon={lockedModalKey ? FEATURE_META[lockedModalKey]?.icon : null}
                gradientClass={lockedModalKey ? FEATURE_META[lockedModalKey]?.gradient : ''}
            />
        </nav>
    );
};

export default Navbar;
